import type { MetaFunction, LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Link, useLoaderData, useRouteError, isRouteErrorResponse } from "@remix-run/react";
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import {getRepoResults} from '../server/topics.server';
import topicPageJson from '../server/topics-data/topicPage.json';
import { RemixRepoList } from '../components/remix-repo-list';
import {apiCacheControlHeader} from '../server/utils.server';

export const meta: MetaFunction = ({ data, params }: { data: any, params: any }) => {
  // Defaults for when data is `undefined`
  let title = 'Related Repos', description = 'Topic page';

  if (data) {
    title = `Top ${data.listTitle} open source projects`;
    const top6Results = (data?.repoNames ?? []).slice(0, 6).map(repoName => repoName.split('/')[1]);
    description = `${data.listTitle} library alternatives: ${top6Results[0]} vs ${top6Results[1]}. ${top6Results[2]} vs ${top6Results[3]}`;
    const descriptionAdditional = `. ${top6Results[4]} vs ${top6Results[5]}`;
    if (description.length + descriptionAdditional.length <= 160) {
      description = description + descriptionAdditional;
    }
  }

  const canonicalURL = `${__BASE_URL__}/topics/${params.topicName}`;
  const jsonLD = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": canonicalURL
  };

  return [
    { title },
    { name: "description", content: description },
    { tagName: "link", rel: "canonical", href: canonicalURL },
    { "script:ld+json": jsonLD },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonicalURL },
    { property: "og:image", content: __OG_IMAGE__ }
  ];
};

export async function loader({params}: LoaderFunctionArgs) {
  const topic = topicPageJson[params.topicName as string];

  if(!topic) {
    throw new Response(null, {
      status: 404,
      statusText: "Not Found",
    });
  }

  topic.repoResults = getRepoResults(topic.repoNames);
  
  return json(topic, { headers: apiCacheControlHeader });
};

export default function Index() {
  const loaderData: any = useLoaderData();

  return (
    <>
      <div className="background-gray px-2 py-2 border border-gray-100 rounded-md">
        <div className="flex items-center py-1">
          <h1 className="text-xl font-bold mr-1 ">
            {loaderData.listTitle}
          </h1>
          <div className="tooltip flex items-center">
            <span className="tooltip-content">View on GitHub</span>
            <a href={`https://github.com/topics/${loaderData.topicName}`} target="_blank" className="ml-1 inline-flex items-center" aria-label="View on GitHub">
              <ArrowTopRightOnSquareIcon className="inline-block size-5 text-gray-400" />
            </a>
          </div>
        </div>
        <p >
          {loaderData.overview_text}
        </p>
        <p className="mt-4">
          View the most prominent open source {loaderData.listTitle} projects in the list below. Click on a specific project to view its alternative or complementary packages. Make comparisons and find the best package for your app.
        </p>
      </div>

      <div className="pt-3 text-lg font-semibold">Popular {loaderData.listTitle} repositories:</div>

      <hr />

      <RemixRepoList repoResults={loaderData.repoResults} />

    </>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
      <div>
        <h1 className="text-xl font-bold mb-2">
          Topic not found
        </h1>
        <p>We could not find that topic in our system. Please return to the home page to select another topic.</p>
      </div>
      )
    }
    else {
      return (
        <div>
          <h1>
            {error.status} {error.statusText}
          </h1>
          <p>{error.data}</p>
        </div>
      );
    }
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </div>
    );
  } else {
    return <h1>Unknown Error</h1>;
  }
}
